import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  KnowledgeItem,
  KnowledgeItemPart,
} from '@api/models/knowledge-item.model';
import { InstallationVariantOption } from '@api/services/knowledge-item.service';
import { KnowledgeItemService } from '@api/services/knowledge-item.service';
import { FileUploadComponent } from '@ui/file-upload/file-upload.component';
import { InputComponent } from '@ui/input/input.component';
import { ModalComponent } from '@ui/modal/modal.component';
import { MODAL_REF, ModalRef, ModalService } from '@ui/modal/modal.service';
import { NotificationService } from '@ui/notification/notification.service';
import { SelectComponent } from '@ui/select/select.component';
import { LoadingService } from '@ui/app-layout/loading-service';

const MAX_BYTES = 25600 * 1024;
const ACCEPT = '.pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.xls,.xlsx';

@Component({
  selector: 'app-knowledge-item-modal',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    ReactiveFormsModule,
    InputComponent,
    SelectComponent,
    FileUploadComponent,
  ],
  template: `
    <ui-modal
      [title]="item ? 'Item bewerken' : 'Item toevoegen'"
      [submitText]="'Opslaan'"
      [cancelText]="'Annuleren'"
      (close)="closeModal()"
      (save)="saveModal()"
    >
      <form [formGroup]="form" class="space-y-4">
        <ui-input
          id="title"
          label="Titel"
          placeholder="Bijv. Handleiding Stannah rail"
          formControlName="title"
          [required]="true"
        />

        <ui-select
          id="type"
          label="Type"
          formControlName="type"
          [options]="typeOptions"
          [required]="true"
        />

        <ui-select
          id="component"
          label="Onderdeel"
          formControlName="component"
          [options]="componentOptions"
          [required]="true"
        />

        @if (!isGeneral()) {
          <ui-select
            id="manufacturer"
            label="Fabrikant"
            formControlName="manufacturer"
            [options]="manufacturerOptions()"
            placeholder="Alle fabrikanten"
          />
          <ui-select
            id="productType"
            label="Type"
            formControlName="productType"
            [options]="productTypeOptions()"
            [disabled]="!manufacturerSelected()"
            placeholder="Alle types"
          />
          <p class="text-xs text-slate-500">
            Zonder fabrikant is het item zichtbaar voor alle klanten. Alleen een
            fabrikant geldt voor alle types van dit onderdeel.
          </p>
        }

        @if (isLink()) {
          <ui-input
            id="url"
            label="YouTube-URL"
            placeholder="https://www.youtube.com/watch?v=..."
            formControlName="url"
            [required]="true"
          />
          <p class="text-xs text-slate-500">
            Plak een YouTube-link. De video wordt in de klanten-app afgespeeld,
            zonder de app te verlaten.
          </p>
        } @else {
          <ui-file-upload
            formControlName="files"
            [multiple]="false"
            [accept]="accept"
            [maxFileSize]="maxFileSize"
            [helperText]="fileHelperText()"
          />
        }
      </form>
    </ui-modal>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KnowledgeItemModalComponent implements OnInit {
  item: KnowledgeItem | null = null;

  private fb = inject(FormBuilder);
  private modalRef = inject(MODAL_REF);
  private modalService = inject(ModalService);
  private knowledgeItemService = inject(KnowledgeItemService);
  private loadingService = inject(LoadingService);
  private notificationService = inject(NotificationService);

  readonly accept = ACCEPT;
  readonly maxFileSize = MAX_BYTES;
  readonly typeOptions = [
    { label: 'Bestand', value: 'file' },
    { label: 'YouTube-video', value: 'link' },
  ];
  readonly componentOptions = [
    { label: 'Rail', value: 'rail' },
    { label: 'Unit (stoel)', value: 'unit' },
    { label: 'Algemeen', value: 'general' },
  ];

  form = this.fb.group({
    title: ['', Validators.required],
    type: ['file' as 'file' | 'link', Validators.required],
    component: ['unit' as KnowledgeItemPart, Validators.required],
    manufacturer: [''],
    productType: [''],
    url: [''],
    files: [[] as File[]],
  });

  variants = signal<InstallationVariantOption[]>([]);
  typeSignal = signal<'file' | 'link'>('file');
  componentSignal = signal<KnowledgeItemPart>('unit');
  manufacturerSignal = signal('');
  isLink = computed(() => this.typeSignal() === 'link');
  isGeneral = computed(() => this.componentSignal() === 'general');
  manufacturerSelected = computed(() => this.manufacturerSignal() !== '');

  manufacturerOptions = computed(() => {
    const names = new Set<string>();
    for (const variant of this.variants()) {
      if (variant.component === this.componentSignal()) {
        names.add(variant.manufacturer);
      }
    }
    const current = this.manufacturerSignal();
    if (current) {
      names.add(current);
    }
    return [...names]
      .sort((a, b) => a.localeCompare(b, 'nl'))
      .map((name) => ({ label: name, value: name }));
  });

  productTypeOptions = computed(() => {
    const manufacturer = this.manufacturerSignal();
    const types = new Set<string>();
    for (const variant of this.variants()) {
      if (
        variant.component === this.componentSignal() &&
        variant.manufacturer === manufacturer
      ) {
        types.add(variant.product_type);
      }
    }
    const current = this.form.controls.productType.value;
    if (current) {
      types.add(current);
    }
    return [...types]
      .sort((a, b) => a.localeCompare(b, 'nl'))
      .map((type) => ({ label: type, value: type }));
  });

  ngOnInit() {
    if (this.item) {
      this.form.patchValue({
        title: this.item.title,
        type: this.item.type,
        component: this.item.component,
        manufacturer: this.item.manufacturer ?? '',
        productType: this.item.product_type ?? '',
        url: this.item.url ?? '',
        files: [],
      });
      this.typeSignal.set(this.item.type);
      this.componentSignal.set(this.item.component);
      this.manufacturerSignal.set(this.item.manufacturer ?? '');
    }

    this.form.controls.type.valueChanges.subscribe((type) => {
      if (type === 'file' || type === 'link') {
        this.typeSignal.set(type);
      }
    });

    this.form.controls.component.valueChanges.subscribe((component) => {
      if (component === 'rail' || component === 'unit' || component === 'general') {
        this.componentSignal.set(component);
      }
      this.form.patchValue({ manufacturer: '', productType: '' }, { emitEvent: false });
      this.manufacturerSignal.set('');
    });

    this.form.controls.manufacturer.valueChanges.subscribe((manufacturer) => {
      const value = manufacturer ?? '';
      this.manufacturerSignal.set(value);
      const stillValid = this.productTypeOptions().some(
        (option) => option.value === this.form.controls.productType.value
      );
      if (!value || !stillValid) {
        this.form.controls.productType.setValue('', { emitEvent: false });
      }
    });

    this.knowledgeItemService.getVariants().subscribe({
      next: (response) => this.variants.set(response.data),
      error: () =>
        this.notificationService.error('Fabrikanten en types laden mislukt.'),
    });
  }

  fileHelperText(): string {
    if (this.item?.type === 'file' && this.item.original_filename) {
      return `Huidig bestand: ${this.item.original_filename}. Upload alleen om te vervangen.`;
    }
    return 'PDF, Word, Excel, afbeeldingen of tekst (max. ca. 25 MB).';
  }

  closeModal() {
    this.modalService.close(this.modalRef as ModalRef<unknown>);
  }

  saveModal() {
    this.form.markAllAsTouched();
    const value = this.form.getRawValue();
    const type = value.type as 'file' | 'link';

    if (!value.title || !value.component || !type) {
      this.notificationService.error('Vul de verplichte velden in.');
      return;
    }

    if (type === 'link' && !value.url) {
      this.notificationService.error('YouTube-URL is verplicht bij een video.');
      return;
    }

    const files = (value.files ?? []) as File[];
    if (type === 'file' && !this.item && !files.length) {
      this.notificationService.error('Selecteer een bestand.');
      return;
    }
    if (type === 'file' && this.item?.type !== 'file' && !files.length) {
      this.notificationService.error('Selecteer een bestand.');
      return;
    }

    const payload = {
      title: value.title,
      type,
      component: value.component as KnowledgeItemPart,
      manufacturer: value.component === 'general' ? null : value.manufacturer?.trim() || null,
      product_type:
        value.component === 'general' || !value.manufacturer?.trim()
          ? null
          : value.productType?.trim() || null,
      url: type === 'link' ? value.url : null,
      file: type === 'file' && files[0] ? files[0] : null,
    };

    this.loadingService.show();
    const request$ = this.item
      ? this.knowledgeItemService.update(this.item.id, payload)
      : this.knowledgeItemService.create(payload);

    request$.subscribe({
      next: () => {
        this.loadingService.hide();
        this.notificationService.success(
          this.item ? 'Item bijgewerkt.' : 'Item toegevoegd.'
        );
        this.modalService.close(this.modalRef as ModalRef<unknown>);
      },
      error: () => {
        this.loadingService.hide();
        this.notificationService.error('Opslaan mislukt. Controleer de invoer.');
      },
    });
  }
}
